import React, {
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from 'react';
import EmployeesAPI from '../../services/api/admin/employees-api';
import { ListBoxComponent } from '@syncfusion/ej2-react-dropdowns';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-react-dropdowns/styles/material.css';

const DualListBox = forwardRef((props, ref) => {
  const availableListRef = useRef(null);
  const assignedListRef = useRef(null);

  // Available employees (right box)
  const [availableEmployees, setAvailableEmployees] = useState([]);

  // Assigned employees (left box)
  const [assignedEmployees, setAssignedEmployees] = useState([]);

  // Fetch unassigned users when foreman changes
  useEffect(() => {
    const getUnassignedUsers = async () => {
      try {
        const employees = await EmployeesAPI.getUnassigned(
          props.selectedForeman?.id,
          props.authToken
        );

        // Database query already excludes the foreman, so no need to filter here
        const users = employees.map((user) => ({
          text: `${user.firstname} ${user.lastname}`,
          id: user.id,
        }));

        setAvailableEmployees(users);
      } catch (err) {
        console.error('Error fetching unassigned users:', err);
      }
    };

    if (props.authToken) {
      getUnassignedUsers();
    }
  }, [props.authToken, props.selectedForeman]);

  // Fetch assigned users for the selected foreman
  useEffect(() => {
    const getAssignedUsers = async () => {
      try {
        const employees = await EmployeesAPI.getForemanAssigned(
          props.selectedForeman.id,
          props.authToken
        );

        const users = employees.map((user) => ({
          text: `${user.firstname} ${user.lastname}`,
          id: user.id,
        }));

        setAssignedEmployees(users);
      } catch (err) {
        console.error('Error fetching assigned users:', err);
      }
    };

    if (props.authToken && props.selectedForeman?.id) {
      getAssignedUsers();
    } else {
      setAssignedEmployees([]);
    }
  }, [props.authToken, props.selectedForeman]);

  const fields = { text: 'text', value: 'id' };

  // Don't create filtered arrays - let Syncfusion handle filtering internally

  const onDropAvailable = (args) => {
    // Just allow the drag - no API call yet
    // Will be saved when user clicks Update button
  };

  const onDropAssigned = (args) => {
    // Just allow the drag - no API call yet
    // Will be saved when user clicks Update button
  };

  // Expose the current state and save function to parent component
  useImperativeHandle(ref, () => ({
    getValues: () => {
      // Get all list items and extract their data
      const availableItems = availableListRef.current?.getItems() || [];
      const assignedItems = assignedListRef.current?.getItems() || [];

      const availableData = Array.from(availableItems).map((item) => {
        const id = item.getAttribute('data-value') || item.id;
        const text = item.textContent || item.innerText;
        return { id, text };
      });

      const assignedData = Array.from(assignedItems).map((item) => {
        const id = item.getAttribute('data-value') || item.id;
        const text = item.textContent || item.innerText;
        return { id, text };
      });

      return {
        available: availableData,
        assigned: assignedData,
      };
    },
    saveAssignments: async () => {
      // Save the current state to the database
      try {
        const assignedItems = assignedListRef.current?.getItems() || [];
        const currentAssignedIds = Array.from(assignedItems).map(
          (item) => item.getAttribute('data-value') || item.id
        );

        // Get the original assigned user IDs
        const originalAssignedIds = assignedEmployees.map((emp) => emp.id);

        // Find users to assign (in current but not in original)
        const toAssign = currentAssignedIds.filter(
          (id) => !originalAssignedIds.includes(id)
        );

        // Find users to unassign (in original but not in current)
        const toUnassign = originalAssignedIds.filter(
          (id) => !currentAssignedIds.includes(id)
        );

        // Process assignments
        for (const employeeId of toAssign) {
          await EmployeesAPI.assignToCrew(
            employeeId,
            props.selectedForeman?.id,
            props.authToken
          );
        }

        // Process unassignments
        for (const employeeId of toUnassign) {
          await EmployeesAPI.unassignFromCrew(
            employeeId,
            props.selectedForeman?.id,
            props.authToken
          );
        }

        return {
          success: true,
          assigned: toAssign.length,
          unassigned: toUnassign.length,
        };
      } catch (err) {
        console.error('Error saving assignments:', err);
        throw err;
      }
    },
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 3,
        justifyContent: 'center',
        padding: 3,
        paddingTop: 2,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
        <Typography variant="h6" gutterBottom>
          Available Employees
        </Typography>
        <ListBoxComponent
          ref={availableListRef}
          dataSource={availableEmployees}
          fields={fields}
          allowDragAndDrop={true}
          allowFiltering={true}
          filtering={(e) => {
            if (!e.text) {
              e.updateData(availableEmployees);
              return;
            }

            const query = e.text.toLowerCase();
            const filteredData = availableEmployees.filter((item) => {
              // Split the search query into words to match any part
              const searchTerms = query.split(' ').filter((term) => term);
              const itemText = item.text.toLowerCase();

              // Check if all search terms are found in the item text
              return (
                searchTerms.every((term) => itemText.includes(term)) ||
                // Or check if the full query matches
                itemText.includes(query)
              );
            });

            e.updateData(filteredData);
          }}
          scope="combined-list"
          height="400px"
          drop={onDropAvailable}
          change={(e) => {
            // Sync state after drag operations
            if (e.isInteracted) {
              setTimeout(() => {
                const currentItems = availableListRef.current?.getItems() || [];
                const currentIds = Array.from(currentItems).map(
                  (item) => item.getAttribute('data-value') || item.id
                );

                // Update state to match what's in the UI
                const allEmployees = [
                  ...availableEmployees,
                  ...assignedEmployees,
                ];
                setAvailableEmployees(
                  allEmployees.filter((emp) => currentIds.includes(emp.id))
                );
                setAssignedEmployees(
                  allEmployees.filter((emp) => !currentIds.includes(emp.id))
                );
              }, 50);
            }
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
        <Typography variant="h6" gutterBottom>
          Current Crew{' '}
          {/* {props.selectedForeman
            ? `${props.selectedForeman.firstname} ${props.selectedForeman.lastname}`
            : ""} */}
        </Typography>
        <ListBoxComponent
          ref={assignedListRef}
          dataSource={assignedEmployees}
          fields={fields}
          allowDragAndDrop={true}
          allowFiltering={true}
          filtering={(e) => {
            if (!e.text) {
              e.updateData(assignedEmployees);
              return;
            }

            const query = e.text.toLowerCase();
            const filteredData = assignedEmployees.filter((item) => {
              // Split the search query into words to match any part
              const searchTerms = query.split(' ').filter((term) => term);
              const itemText = item.text.toLowerCase();

              // Check if all search terms are found in the item text
              return (
                searchTerms.every((term) => itemText.includes(term)) ||
                // Or check if the full query matches
                itemText.includes(query)
              );
            });

            e.updateData(filteredData);
          }}
          scope="combined-list"
          height="400px"
          drop={onDropAssigned}
          change={(e) => {
            // Sync state after drag operations
            if (e.isInteracted) {
              setTimeout(() => {
                const currentItems = assignedListRef.current?.getItems() || [];
                const currentIds = Array.from(currentItems).map(
                  (item) => item.getAttribute('data-value') || item.id
                );

                // Update state to match what's in the UI
                const allEmployees = [
                  ...availableEmployees,
                  ...assignedEmployees,
                ];
                setAssignedEmployees(
                  allEmployees.filter((emp) => currentIds.includes(emp.id))
                );
                setAvailableEmployees(
                  allEmployees.filter((emp) => !currentIds.includes(emp.id))
                );
              }, 50);
            }
          }}
        />
      </Box>
    </Box>
  );
});

DualListBox.displayName = 'DualListBox';

export default DualListBox;
